import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Course, Lesson } from './models/Course';
import {
  getAssignedLessonIds,
  getCourseList,
  getLessons,
} from './services/CourseService';
import { LoadingIndicator, Routes } from '@xolv-mybrightlink/common';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';

const MAX_ASSIGNED_LESSONS = 3;

function LessonCard({
  children,
  onLessonClicked,
}: {
  children?: React.ReactElement;
  onLessonClicked?: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'inline-block',
        flex: 1,
        p: 5,
        borderRadius: 3,
        boxSizing: 'border-box',
        boxShadow:
          '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
        border: '1px solid #d8dade',
        maxWidth: '31%',
        bgcolor: '#fff',
        cursor: 'pointer',
      }}
      onClick={onLessonClicked}
    >
      {children}
    </Box>
  );
}

function ArrowButton({
  onClick,
  sx,
  children,
}: {
  onClick: () => void;
  sx?: SxProps<Theme>;
  children?: React.ReactElement;
}) {
  return (
    <Box
      sx={{
        height: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        top: 0,
        zIndex: 1,
        ...sx,
      }}
    >
      <Box
        sx={{
          p: 0.25,
          borderRadius: '100vw',
          border: '0.5px solid #d8dade',
          bgcolor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onClick={onClick}
      >
        {children}
      </Box>
    </Box>
  );
}

export function CoursePage() {
  const intl = useIntl();

  const [courseList, setCourseList] = useState<Course[]>([]);
  const [assignedLessons, setAssignedLessons] = useState<Lesson[]>([]);
  const [courseLoading, setCourseLoading] = useState<boolean>(false);
  const [lessonLoading, setLessonLoading] = useState<boolean>(true);
  const [firstCourse, setFirstCourse] = useState<Course | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    (async function () {
      try {
        setCourseLoading(true);
        setLessonLoading(true);
        const courses = await getCourseList();
        setCourseList(courses);
        if (courses.length > 0) {
          const ipCourses = courses.filter((c) => c.completed < c.numOfLessons);
          setFirstCourse(ipCourses[0]);
        }
      } finally {
        setCourseLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async function () {
      try {
        const assignedLessonIds = await getAssignedLessonIds('en');
        const lessons = await getLessons(assignedLessonIds);

        const sortedLessons = _.sortBy(lessons, (lesson) => {
          const courseIndex = _.findIndex(
            courseList,
            (course) => course.id === lesson.courseId,
          );
          return `${courseIndex}${lesson.title}`;
        });

        setAssignedLessons(sortedLessons);
      } finally {
        setLessonLoading(false);
      }
    })();
  }, [courseList]);

  const displayIndexes = _.range(MAX_ASSIGNED_LESSONS);
  const displayCourses = firstCourse
    ? courseList.filter((course) => course.id !== firstCourse.id)
    : courseList;

  return (
    <Box>
      <Typography
        variant='h6'
        sx={{
          p: 10,
          fontWeight: 500,
        }}
      >
        <FormattedMessage id='sidebar.menu.parent-led-curriculum' />
      </Typography>
      <Box sx={{ px: 10, borderRadius: 3, overflow: 'hidden' }}>
        {assignedLessons.length > 0 && (
          <Typography
            variant='h6'
            sx={{ color: '#3b4559', fontWeight: 600, mt: 10, mb: 4 }}
          >
            <FormattedMessage id='resources.courses.assigned.lessons' />
          </Typography>
        )}

        <Box sx={{ position: 'relative' }}>
          {lessonLoading && <LoadingIndicator sx={{ py: 10 }} />}

          {assignedLessons.length > 0 && (
            <>
              <ArrowButton
                sx={{ left: -15 }}
                onClick={() => {
                  setCurrentIndex((currentIndex) =>
                    Math.max(0, currentIndex - 1),
                  );
                }}
              >
                <KeyboardArrowLeftIcon />
              </ArrowButton>

              <ArrowButton
                sx={{ right: -15 }}
                onClick={() => {
                  setCurrentIndex((currentIndex) =>
                    Math.min(
                      currentIndex + 1,
                      assignedLessons.length - MAX_ASSIGNED_LESSONS,
                    ),
                  );
                }}
              >
                <KeyboardArrowRightIcon />
              </ArrowButton>
            </>
          )}

          <Box>
            {assignedLessons.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  borderRadius: 3,
                }}
              >
                {displayIndexes
                  .map(
                    (step) =>
                      assignedLessons[
                        (currentIndex + step) % assignedLessons.length
                      ],
                  )
                  .map((item, index) => (
                    <LessonCard
                      key={item.id}
                      onLessonClicked={() =>
                        navigate(
                          Routes.resources.courses.lessonId(
                            item.courseId,
                            item.id,
                          ),
                        )
                      }
                    >
                      <>
                        <Box className={`cursor-${index}`}></Box>
                        <Typography
                          variant='body1'
                          noWrap
                          sx={{
                            color: '#307fe2',
                            fontWeight: 500,
                          }}
                        >
                          {item.courseTitle}
                        </Typography>
                        <Typography
                          variant='body2'
                          noWrap
                          sx={{
                            mt: 2.5,
                            color: '#3b4559',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {_.unescape(item.title)}
                        </Typography>
                      </>
                    </LessonCard>
                  ))}
              </Box>
            )}
          </Box>
        </Box>

        <Typography
          variant='h6'
          sx={{ color: '#3b4559', fontWeight: 600, mt: 10, mb: 4 }}
        >
          <FormattedMessage id='resources.education.courses' />
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '5%',
            boxSizing: 'border-box',
          }}
        >
          {courseLoading && <LoadingIndicator sx={{ py: 50 }} />}

          {firstCourse && (
            <Box
              sx={{
                width: 1,
                height: '30vh',
                background: `url(${firstCourse?.thumbnail})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 3,
                position: 'relative',
                mb: 10,
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate(Routes.resources.courses.courseId(firstCourse?.id));
              }}
            >
              <Box
                sx={{
                  width: 1,
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  px: 10,
                  py: 5,
                }}
              >
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='flex-end'
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 3.75,
                        border: 'solid 1px #b1b5bd',
                        bgcolor: 'rgba(59, 69, 89, 0.8)',
                        width: 'fit-content',
                        px: 2.5,
                        py: 1,
                        cursor: 'pointer',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{ color: '#fff', fontWeight: 500 }}
                      >
                        <FormattedMessage id='resources.courses.continue' />
                      </Typography>
                    </Box>

                    <Typography
                      variant='h5'
                      sx={{ fontSize: '2rem', color: '#fff', mt: 6 }}
                    >
                      {_.unescape(firstCourse?.title)}
                    </Typography>
                    <Typography variant='body2' sx={{ mt: 5, color: '#fff' }}>
                      {intl.formatMessage(
                        { id: 'resources.lessons.completed' },
                        {
                          completed: firstCourse?.completed ?? 0,
                          total: firstCourse?.numOfLessons ?? 0,
                        },
                      )}
                    </Typography>
                  </Box>

                  {(firstCourse?.percentage ?? 0) > 0 && (
                    <Box
                      sx={{
                        width: '20%',
                        height: 20,
                        color: '#edf0f5',
                        borderRadius: 3,
                        overflow: 'hidden',
                        bgcolor: 'rgba(98, 106, 122, 0.8)',
                      }}
                    >
                      <Box
                        sx={{
                          width: firstCourse?.percentage / 100,
                          height: 20,
                          background: '#00b2a9',
                          borderRadius: 3,
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            </Box>
          )}

          {displayCourses.map((course) => (
            <Box
              key={course.title}
              sx={{
                width: 0.3,
                boxSizing: 'border-box',
                mb: 10,
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate(Routes.resources.courses.courseId(course.id));
              }}
            >
              <Box
                key={course.title}
                sx={{
                  width: 1,
                  height: '150px',
                  background: `url(${course.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '12px',
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant='body1'
                  sx={{ mt: 5, fontWeight: 600, color: '#3b4559' }}
                >
                  {course.title}
                </Typography>
                <Box
                  sx={{
                    mt: 5.5,
                    flex: 1,
                    gap: 2,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'flex',
                  }}
                >
                  <Typography variant='body2' sx={{ color: '#3b4559' }}>
                    {intl.formatMessage(
                      { id: 'resources.lessons.completed' },
                      {
                        completed: course?.completed ?? 0,
                        total: course?.numOfLessons ?? 0,
                      },
                    )}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      maxWidth: 0.4,
                      flex: 1,
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    {course?.completed === course?.numOfLessons && (
                      <img
                        src='/public/assets/icons/ic_check_circle.svg'
                        style={{
                          width: 24,
                          height: 24,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        color: '#edf0f5',
                        flex: 1,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '0.5px solid #edf0f5',
                        bgcolor: '#edf0f5',
                      }}
                    >
                      <Box
                        sx={{
                          width: course.percentage / 100,
                          height: 20,
                          background: '#00b2a9',
                          borderRadius: 3,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
